<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import wootConstants from 'dashboard/constants/globals';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  DropdownContainer,
  DropdownBody,
  DropdownItem,
} from 'dashboard/components-next/dropdown-menu/base';

defineProps({
  src: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change', 'delete']);

const { t } = useI18n();
const store = useStore();
const { isImpersonating } = useImpersonation();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() =>
  [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ].map((label, index) => ({
    label,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', {
      class: [statusColors[index], 'size-2.5 rounded-full shrink-0'],
    }),
  }))
);

const activeStatus = computed(
  () =>
    availabilityStatuses.value.find(
      status => status.value === currentUserAvailability.value
    ) || availabilityStatuses.value[0]
);

const changeAvailabilityStatus = availability => {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
};

const updateProfilePicture = e => emit('change', e);
const deleteProfilePicture = () => emit('delete');
</script>

<template>
  <div class="flex flex-col items-center gap-5">
    <Avatar
      :src="src || ''"
      :name="name || ''"
      :size="88"
      allow-upload
      @upload="updateProfilePicture"
      @delete="deleteProfilePicture"
    />
    <DropdownContainer class="w-full">
      <template #trigger="{ toggle }">
        <button
          type="button"
          class="reset-base flex h-8 w-full items-center justify-between gap-2 rounded-lg bg-n-slate-9/10 px-3 text-sm font-medium text-n-slate-12 hover:bg-n-slate-9/20 focus:outline-none"
          @click="toggle"
        >
          <span class="flex min-w-0 items-center gap-2">
            <span
              class="size-2.5 rounded-full shrink-0"
              :class="activeStatus.color"
            />
            <span class="truncate">{{ activeStatus.label }}</span>
          </span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-4 text-n-slate-11 shrink-0"
          />
        </button>
      </template>
      <DropdownBody class="w-44 z-20">
        <DropdownItem
          v-for="status in availabilityStatuses"
          :key="status.value"
          :label="status.label"
          :icon="status.icon"
          class="cursor-pointer"
          @click="changeAvailabilityStatus(status.value)"
        />
      </DropdownBody>
    </DropdownContainer>
  </div>
</template>
